/**
 * FR-6's **Rank type** segmented control — Organic / Sponsored / Both — and the `localStorage`-backed
 * preference behind it.
 *
 * The module owns both halves of the preference so there is exactly one place that knows the storage
 * key and the default:
 *
 * - `useRankTypePreference()` is the owner of the value. `RankHeatmapTable/index.tsx` (`WEB-004`)
 *   calls it once and threads the value into this control, into the rows and into `RankLegend` —
 *   which is why the control itself is controlled rather than self-contained: three consumers read
 *   the value, so one of them holding it privately would need the other two to guess.
 * - `RankTypeToggle` renders it.
 *
 * The choice is **per browser, not per brand** (`product/rank-tracker-journeys.md § J1`): the key
 * carries no brand id, so a brand switch — which resets every other control on the screen — leaves
 * the rank type alone, and so does a remount.
 *
 * The one-time "Sponsored rank is here" callout is **slice C** (AUT-497), now live: the `Popover`'s
 * `open` is driven off `calloutOpen` and the real `PopoverContent` is a sibling of the
 * `PopoverAnchor`. The open/seen/suppress/launch-window state lives in the parent
 * (`RankHeatmapTable/index.tsx`); this control only renders the body and routes every close through
 * `onCalloutDismiss`.
 */

import { useCallback, useState } from "react";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export type RankType = "organic" | "sponsored" | "both";

/**
 * The single `localStorage` key the preference lives under. Deliberately brand-free — see the
 * module comment and `product/rank-tracker-journeys.md § J1`.
 */
export const RANK_TYPE_STORAGE_KEY = "signal.rank-tracker.rank-type";

/**
 * Slice C (AUT-497) — the `localStorage` key the one-time sponsored-rank callout's "seen" flag lives
 * under (value `"1"`). Kebab-case, beside `RANK_TYPE_STORAGE_KEY`, so one place owns storage keys
 * (Decision D-2 — not the prototype's camelCase `signal.rankTracker.sponsoredCalloutSeen`).
 */
export const SPONSORED_CALLOUT_SEEN_KEY = "signal.rank-tracker.sponsored-callout-seen";

/**
 * Slice C (AUT-497) — the callout stops showing for everyone 30 days after launch (FR-6, Decision
 * D-1): a single fixed calendar cutoff, not a per-browser first-render timer. The value is the day
 * slice C reached production — release #436 "Release 16.10", merged to `staging` 2026-10-16 (AUT-583)
 * — so the callout stops showing at 2026-11-15T00:00:00Z.
 */
export const LAUNCH_DATE = "2026-10-16T00:00:00.000Z";

/** Organic is the view the screen has always had, so it is what an unset preference reads as. */
export const DEFAULT_RANK_TYPE: RankType = "organic";

/** The three options, in the order the design puts them in. */
const RANK_TYPE_OPTIONS: ReadonlyArray<{ value: RankType; label: string }> = [
  { value: "organic", label: "Organic" },
  { value: "sponsored", label: "Sponsored" },
  { value: "both", label: "Both" },
];

function isRankType(value: unknown): value is RankType {
  return value === "organic" || value === "sponsored" || value === "both";
}

/**
 * The stored choice, or `DEFAULT_RANK_TYPE` when there is nothing usable to read — no entry, a value
 * written by an older build, or storage that refuses to be read at all (private mode, disabled site
 * data). A preference is a convenience: never let it fail the screen.
 */
function readStoredRankType(): RankType {
  if (typeof window === "undefined") {
    return DEFAULT_RANK_TYPE;
  }

  try {
    const stored = window.localStorage.getItem(RANK_TYPE_STORAGE_KEY);
    return isRankType(stored) ? stored : DEFAULT_RANK_TYPE;
  } catch {
    return DEFAULT_RANK_TYPE;
  }
}

function writeStoredRankType(rankType: RankType): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(RANK_TYPE_STORAGE_KEY, rankType);
  } catch {
    // Storage is unavailable or full. The in-memory choice still applies for this visit.
  }
}

/**
 * Slice C — whether the one-time callout has already been seen/dismissed in this browser. Defaults to
 * `false` (shown-if-unseen) when storage cannot be read (private mode, disabled site data): a storage
 * failure must never fail the screen (NFR-2), mirroring `readStoredRankType`.
 */
export function readCalloutSeen(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return window.localStorage.getItem(SPONSORED_CALLOUT_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

/** Slice C — record the callout as seen for this browser. A storage failure is swallowed (NFR-2). */
export function writeCalloutSeen(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(SPONSORED_CALLOUT_SEEN_KEY, "1");
  } catch {
    // Storage is unavailable or full. The callout is already hidden in-memory for this visit.
  }
}

/**
 * The rank-type preference: the current value, and the setter that both applies and persists it.
 * Written on selection rather than in an effect, so a mount that changes nothing writes nothing.
 */
export function useRankTypePreference(): [RankType, (next: RankType) => void] {
  const [rankType, setRankType] = useState<RankType>(readStoredRankType);

  const selectRankType = useCallback((next: RankType) => {
    setRankType(next);
    writeStoredRankType(next);
  }, []);

  return [rankType, selectRankType];
}

export interface RankTypeToggleProps {
  value: RankType;
  onChange: (next: RankType) => void;
  /** Slice C — the one-time callout's open state. The parent computes it from seen/suppress/window. */
  calloutOpen?: boolean;
  /** Slice C — dismissal of the one-time callout: marks it seen in the parent and retires it. */
  onCalloutDismiss?: () => void;
}

/** The "Rank type" segmented control: Organic / Sponsored / Both, Organic first and default. */
export function RankTypeToggle({ value, onChange, calloutOpen = false, onCalloutDismiss }: RankTypeToggleProps) {
  return (
    // Slice C (AUT-497): the one-time callout grows into the popover slice A left pinned closed.
    // `open` is driven off `calloutOpen`; the `PopoverContent` below is a sibling of the anchor, not
    // a child of the control. The popover is controlled with no `onOpenChange`, so every close path
    // goes through `onCalloutDismiss` — an outside click cannot silently retire it (FR-2).
    <Popover open={calloutOpen}>
      <PopoverAnchor asChild>
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground">Rank type</span>
          <Tabs
            value={value}
            onValueChange={(next) => {
              // Radix hands back a plain string; only the three the control renders can arrive.
              if (isRankType(next)) {
                onChange(next);
                // FR-2: choosing any toggle option also dismisses the callout.
                onCalloutDismiss?.();
              }
            }}
          >
            <TabsList aria-label="Rank type">
              {RANK_TYPE_OPTIONS.map((option) => (
                <TabsTrigger key={option.value} value={option.value}>
                  {option.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </PopoverAnchor>
      {/* FR-1: anchored above the toggle, right-aligned, ~300px. `avoidCollisions` is left default so
          Radix flips it below when there is no room above. `onInteractOutside` is prevented (FR-2:
          outside-click is not a dismiss); `onOpenAutoFocus` is prevented so opening does not move
          focus into it; Escape dismisses. */}
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-[300px] space-y-3 p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={() => onCalloutDismiss?.()}
      >
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Badge variant="info">New</Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mr-2 -mt-1"
              aria-label="Close"
              onClick={() => onCalloutDismiss?.()}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-base font-semibold text-foreground">Sponsored rank is here</p>
        </div>
        <p className="text-sm text-muted-foreground">
          Choose Both to see organic and sponsored rank together for each keyword.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => onCalloutDismiss?.()}>
            Got it
          </Button>
          <Button
            size="sm"
            onClick={() => {
              // FR-2: "Show me" switches to Both (persisted under RANK_TYPE_STORAGE_KEY via the same
              // onChange the tabs use) and then dismisses.
              onChange("both");
              onCalloutDismiss?.();
            }}
          >
            Show me
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
